import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button, input:not([type="hidden"]), select, textarea, ' +
  'iframe, audio[controls], video[controls], [contenteditable=""], [contenteditable="true"], [tabindex]';

export const getFocusableElements = (root) => {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    if (el.getAttribute("tabindex") === "-1") return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0 && !el.getClientRects().length) return false;
    return true;
  });
};

/**
 * Focus management for custom dialog/sheet/panel components.
 * - On open: remembers the previously focused element, then focuses
 *   the close button (preferred) or the first focusable element.
 * - While open: traps Tab/Shift+Tab inside the dialog and listens for Escape.
 * - On close: restores focus to the element that opened the dialog.
 *
 * @param {object} opts
 * @param {boolean} opts.isOpen Whether the dialog is currently visible.
 * @param {React.RefObject} opts.dialogRef Ref to the dialog root element.
 * @param {string} [opts.closeSelector] Optional selector for the preferred initial focus target.
 * @param {() => void} [opts.onEscape] Optional escape handler.
 */
export const useDialogFocusManagement = ({
  isOpen,
  dialogRef,
  closeSelector,
  onEscape,
}) => {
  const previousFocusRef = useRef(null);
  const focusTimeoutRef = useRef(null);

  const focusInitial = useCallback(() => {
    if (!dialogRef?.current) return;
    let target = null;
    if (closeSelector) {
      target = dialogRef.current.querySelector(closeSelector);
    }
    if (!target) {
      const focusables = getFocusableElements(dialogRef.current);
      target = focusables[0] || null;
    }
    if (!target) {
      dialogRef.current.setAttribute("tabindex", "-1");
      target = dialogRef.current;
    }
    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus?.();
    }
  }, [dialogRef, closeSelector]);

  useEffect(() => {
    if (isOpen) {
      const active = document.activeElement;
      if (active && active !== document.body) {
        previousFocusRef.current = active;
      }
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = setTimeout(focusInitial, 60);
    } else {
      clearTimeout(focusTimeoutRef.current);
      const toRestore = previousFocusRef.current;
      previousFocusRef.current = null;
      if (toRestore && typeof toRestore.focus === "function" && document.body.contains(toRestore)) {
        try {
          toRestore.focus({ preventScroll: true });
        } catch {
          toRestore.focus?.();
        }
      }
    }
    return () => clearTimeout(focusTimeoutRef.current);
  }, [isOpen, focusInitial]);

  // Also restore focus when the dialog unmounts while still "open"
  // (e.g. components that mount on open and unmount on close).
  useEffect(() => {
    return () => {
      const toRestore = previousFocusRef.current;
      previousFocusRef.current = null;
      if (toRestore && typeof toRestore.focus === "function" && document.body.contains(toRestore)) {
        try {
          toRestore.focus({ preventScroll: true });
        } catch {
          toRestore.focus?.();
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        if (typeof onEscape === "function") onEscape();
        return;
      }
      if (e.key !== "Tab" || !dialogRef?.current) return;
      const focusables = getFocusableElements(dialogRef.current);
      if (focusables.length === 0) {
        e.preventDefault();
        try { dialogRef.current.focus({ preventScroll: true }); } catch { /* noop */ }
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const insideDialog = dialogRef.current.contains(active);
      if (e.shiftKey) {
        if (!insideDialog || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!insideDialog || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, dialogRef, onEscape]);
};

export default useDialogFocusManagement;
